import React, { useEffect, useState } from 'react';
import {
  BotChatFooter,
  BotChatTime,
  CheckboxGroup,
  CheckboxGroupContainer,
  TextAreaBox,
  ThumbsContainer,
} from './styled';
import { Modal, Checkbox, Input } from 'antd';
import apptracker from '@src/utils/sensor/apptracker';
import { PROMPT_FEEDBACK } from '@src/utils/sensor/track';
import { noop } from 'lodash';
import moment from 'moment';
import { DislikeOutlined, LikeOutlined } from '@ant-design/icons';

const GOOD_FEEDBACK = 'GOOD';
const BAD_FEEDBACK = 'BAD';
const NEEDS_IMPROVEMENT = 'Needs_Improvement';

const checkboxOptions = [
  { label: 'Accuracy: The response was inaccurate', value: 'accuracy' },
  { label: 'Coherence: The response did not make sense', value: 'coherence' },
  { label: 'Completeness: The question was not answered in full', value: 'completeness' },
  { label: 'Efficiency: The answer was too long', value: 'efficiency' },
];

// get a map of intial values against each feedback parameter listed above
const initialFeedbackOptionsState = checkboxOptions.reduce((acc, cv) => {
  acc[cv.value] = '';
  return acc;
}, {});

// this has all the values that need to be sent to app insights
const initialFeedbackState = {
  quality: '',
  comment: '',
  ...initialFeedbackOptionsState,
};

function FeedbackContainer({ userMsg, botMsg, timestamp, showTime = true, style = {} }) {
  const { TextArea } = Input;
  const time = moment(timestamp).format('hh:mm A');

  const [feedbackObject, setFeedbackObject] = useState(initialFeedbackState);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (feedbackObject.quality) {
      // track event in app insights once quality has been set
      apptracker(PROMPT_FEEDBACK, {
        feedback_question_answer: `${feedbackObject.quality} ||| ${userMsg} ||| ${botMsg}`,
        feedback_category: Object.entries(feedbackObject).find(
          ([, value]) => value === NEEDS_IMPROVEMENT,
        )?.[0],
        quality: feedbackObject.quality,
        comment: feedbackObject.comment,
      });
    }
  }, [feedbackObject, userMsg, botMsg]);

  const quality = feedbackObject.quality;

  const resetState = () => {
    setFeedbackObject(initialFeedbackState);
  };

  const handleCheckedValuesChange = (checkedValues: any[]) => {
    checkedValues.forEach((cv) => {
      setFeedbackObject((prevState) => {
        const newState = {
          ...prevState,
          ...initialFeedbackOptionsState,
        };

        newState[cv] = NEEDS_IMPROVEMENT;

        return newState;
      });
    });
  };

  const openFeedbackModal = (feedbackType) => {
    // open modal only for thumbs down
    if (feedbackType === BAD_FEEDBACK) {
      setIsModalOpen(true);
    }
  };

  const handleCommentChange = (event) => {
    setFeedbackObject((prevState) => ({
      ...prevState,
      comment: event.target.value,
    }));
  };

  const handleModalOk = () => {
    setFeedbackQuality(BAD_FEEDBACK);

    setIsModalOpen(false);
  };

  const handleModalCancel = () => {
    resetState();
    setIsModalOpen(false);
  };

  const handleThumbsUp = () => {
    setFeedbackQuality(GOOD_FEEDBACK);
  };

  const handleThumbsDown = () => {
    openFeedbackModal(BAD_FEEDBACK);
  };

  const setFeedbackQuality = (quality) => {
    setFeedbackObject((prevState) => ({
      ...prevState,
      quality,
    }));
  };

  const TIMESTAMP = showTime ? <BotChatTime>{time}</BotChatTime> : null;

  const FEEDBACK_THUMBS = (
    <ThumbsContainer feedbackSent={!!quality}>
      {quality == GOOD_FEEDBACK ? (
        <LikeOutlined style={{ color: '#52C41A' }} />
      ) : (
        <LikeOutlined style={{ color: '#808284' }} onClick={quality ? noop : handleThumbsUp} />
      )}
      {quality == BAD_FEEDBACK ? (
        <DislikeOutlined style={{ color: '#FF4D4D' }} />
      ) : (
        <DislikeOutlined style={{ color: '#808284' }} onClick={quality ? noop : handleThumbsDown} />
      )}
    </ThumbsContainer>
  );

  return timestamp ? (
    <>
      <BotChatFooter style={style}>{[TIMESTAMP, FEEDBACK_THUMBS]}</BotChatFooter>

      <Modal
        title="Where should we improve?"
        open={isModalOpen}
        okText="Send"
        onOk={handleModalOk}
        onCancel={handleModalCancel}
      >
        <CheckboxGroupContainer>
          <Checkbox.Group onChange={handleCheckedValuesChange}>
            <CheckboxGroup>
              {checkboxOptions.map((option) => (
                <Checkbox value={option.value} key={option.value}>
                  {option.label}
                </Checkbox>
              ))}
            </CheckboxGroup>
          </Checkbox.Group>
        </CheckboxGroupContainer>

        <TextAreaBox>
          <TextArea
            rows={4}
            placeholder="Tell us more"
            maxLength={1000}
            value={feedbackObject.comment}
            allowClear={true}
            onChange={handleCommentChange}
          />
        </TextAreaBox>
      </Modal>
    </>
  ) : null;
}

export default FeedbackContainer;
